import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Paper, Typography, Alert, CircularProgress, Chip } from '@mui/material';
import { API_BASE_URL } from '../config';
import { apiGet, apiPost } from '../utils/api';
import DoctorDesk from './DoctorDesk';
import IndividualBookingForm from './IndividualBookingForm';
import CalendarPanel from './CalendarPanel';
import ActivityTimeTable from './ActivityTimeTable';
import ExternalNavigation from './ExternalNavigation';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';

/**
 * External Doctor Desk - Standalone interface for AIIMS integration
 * 
 * Features:
 * - Accepts JWT token via URL parameter
 * - Validates token with backend
 * - Left sidebar with: Doctor Desk, New Booking, Activities
 * - Guest pre-selected and locked (read-only)
 * - Session timer with warning
 * - Auto-logout on expiry
 * 
 * IMPORTANT: Uses sessionStorage (not localStorage) to prevent conflicts
 * with regular login sessions. External session is cleared when tab closes.
 */
const ExternalDoctorDesk = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [activeTab, setActiveTab] = useState('doctorDesk');
  const [config, setConfig] = useState({ rooms: {}, treatments: [], therapists: {}, equipment: {} });
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sidebarExpanded, setSidebarExpanded] = useState(false); // Sidebar collapsed by default (icons only)
  const [calendarExpanded, setCalendarExpanded] = useState(false); // Calendar fullscreen mode

  // Generate date range for calendar (14 days from today)
  const dateRange = useMemo(() => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }, []);

  // Extract token from URL
  const getTokenFromURL = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  }, []);

  // Authenticate with backend using AIIMS JWT
  const authenticateWithToken = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiPost('/api/auth/external-aiims-login', { token });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Authentication failed');
      }

      const data = await response.json();
      
      // Store access token in sessionStorage (not localStorage) to prevent conflicts
      // with regular login sessions. External session is tab-specific.
      sessionStorage.setItem('externalAccessToken', data.access_token);
      sessionStorage.setItem('externalSession', JSON.stringify({
        guest_id: data.guest_id,
        guest_name: data.guest_name,
        doctor_context: data.doctor_context,
        expires_at: data.expires_at
      }));

      setSessionData({
        guestId: data.guest_id,
        guestName: data.guest_name,
        doctorContext: data.doctor_context,
        expiresAt: new Date(data.expires_at)
      });

      setAuthenticated(true);
      setLoading(false);
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err.message || 'Failed to authenticate. Please try again.');
      setLoading(false);
    }
  }, []);

  // Load config for IndividualBookingForm
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await apiGet('/api/config');
        const data = await res.json();
        setConfig({
          rooms: data.rooms || {},
          treatments: data.treatments || [],
          therapists: data.therapists || {},
          equipment: data.equipment || {}
        });
      } catch (err) {
        console.error('Failed to load config:', err);
      }
    };
    loadConfig();
  }, []);

  // Calculate time remaining
  const updateTimeRemaining = useCallback(() => {
    if (!sessionData?.expiresAt) return;

    const now = new Date();
    const expiry = sessionData.expiresAt;
    const diffMs = expiry - now;
    const diffMinutes = Math.floor(diffMs / 60000);

    setTimeRemaining(diffMinutes);

    // Show warning when 5 minutes or less remain
    if (diffMinutes <= 5 && diffMinutes > 0) {
      setShowWarning(true);
    }

    // Auto-logout when expired
    if (diffMinutes <= 0) {
      handleLogout();
    }
  }, [sessionData]);

  // Initial authentication
  useEffect(() => {
    const token = getTokenFromURL();
    
    if (!token) {
      setError('No authentication token provided. Please access this page via AIIMS portal.');
      setLoading(false);
      return;
    }

    authenticateWithToken(token);
  }, [getTokenFromURL, authenticateWithToken]);

  // Update time remaining every minute
  useEffect(() => {
    if (!authenticated || !sessionData) return;

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [authenticated, sessionData, updateTimeRemaining]);

  // Handle logout
  const handleLogout = () => {
    // Clear external session data from sessionStorage only
    // Do NOT touch localStorage to preserve regular login session
    sessionStorage.removeItem('externalAccessToken');
    sessionStorage.removeItem('externalSession');

    // Redirect to login page
    window.location.href = '/';
  };

  // Handle unauthorized (session expired from backend)
  const handleUnauthorized = () => {
    alert('Your session has expired. You will be redirected to the login page.');
    handleLogout();
  };

  // Handle booking submission from IndividualBookingForm
  const handleBookingSubmit = async (bookingData) => {
    try {
      const token = sessionStorage.getItem('externalAccessToken');
      const response = await apiPost('/api/schedule', bookingData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Booking failed');
      }

      const result = await response.json();
      // Refresh calendar after successful booking
      setRefreshTrigger(prev => prev + 1);
      return result;
    } catch (err) {
      console.error('Booking error:', err);
      throw err;
    }
  };

  // Handle edit booking from CalendarPanel
  const handleEditBooking = (booking) => {
    // For external mode, we can show a simple alert or implement view-only details
    // For now, just log it - could open a details modal later
    console.log('View booking details:', booking);
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f6f6f6',
          gap: 2
        }}
      >
        <CircularProgress size={60} sx={{ color: '#E4C984' }} />
        <Typography variant="h6" sx={{ color: '#666' }}>
          Authenticating...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f6f6f6',
          p: 3
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 600,
            textAlign: 'center',
            border: '2px solid #f44336'
          }}
        >
          <Typography variant="h5" sx={{ color: '#f44336', mb: 2, fontWeight: 600 }}>
            Authentication Failed
          </Typography>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Please contact your system administrator if this problem persists.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'doctorDesk':
        return (
          <DoctorDesk
            externalMode={true}
            lockedGuestId={sessionData?.guestId}
            lockedGuestName={sessionData?.guestName}
            doctorContext={sessionData?.doctorContext}
            onUnauthorized={handleUnauthorized}
          />
        );
      
      case 'booking':
        return (
          <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Left Panel - Booking Form (hidden when calendar is expanded) */}
            {!calendarExpanded && (
              <Box sx={{ 
                width: '380px', 
                minWidth: '350px',
                flexShrink: 0, 
                bgcolor: '#fff', 
                boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                overflow: 'auto',
                borderRight: '1px solid #e5e7eb',
                transition: 'width 0.3s ease'
              }}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#171717' }}>
                    New Booking
                  </Typography>
                  <IndividualBookingForm
                    initialFormData={{
                      guestName: sessionData?.guestName || '',
                      guestId: sessionData?.guestId || '',
                      treatment: '',
                      preferredTime: '',
                      preferredTimeOfDay: 'morning',
                      therapistPreference: '',
                      roomPreference: '',
                      date: selectedDate,
                      genderPreference: ''
                    }}
                    onSubmit={handleBookingSubmit}
                    config={config}
                    isLoading={false}
                    error={null}
                    success={null}
                    isVisible={true}
                    externalMode={true}
                    lockedGuestId={sessionData?.guestId}
                    lockedGuestName={sessionData?.guestName}
                  />
                </Box>
              </Box>
            )}

            {/* Right Panel - Calendar View (takes full width when expanded) */}
            <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0, transition: 'all 0.3s ease' }}>
              <CalendarPanel
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                dateRange={dateRange}
                config={config}
                onEditBooking={handleEditBooking}
                isLoading={false}
                refreshTrigger={refreshTrigger}
                headerVariant="compact"
                showTitle={false}
                filterGuestId={sessionData?.guestId}
                isExpanded={calendarExpanded}
                onToggleExpand={() => setCalendarExpanded(!calendarExpanded)}
              />
            </Box>
          </Box>
        );
      
      case 'activities':
        return (
          <ActivityTimeTable
            externalMode={true}
            readOnly={true}
            lockedGuestId={sessionData?.guestId}
            lockedGuestName={sessionData?.guestName}
          />
        );
      
      default:
        return null;
    }
  };

  // Authenticated - show interface with sidebar
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f6f6f6' }}>
      {/* Left Sidebar Navigation - Collapsible (icons when collapsed) */}
      <ExternalNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onLogout={handleLogout}
        doctorName={sessionData?.doctorContext?.doctor_name}
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Header Bar */}
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 0,
            bgcolor: '#fff',
            borderBottom: '2px solid #E4C984'
          }}
        >
          {/* Left: Guest Info */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {/* Doctor Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ color: '#E4C984', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1 }}>
                  Doctor
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sessionData?.doctorContext?.doctor_name || 'Unknown'}
                </Typography>
              </Box>
            </Box>

            {/* Guest Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon sx={{ color: '#E4C984', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1 }}>
                  Guest (Locked)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sessionData?.guestName || sessionData?.guestId || 'Unknown'}
                  {sessionData?.guestName && sessionData?.guestId && (
                    <span style={{ color: '#666', fontWeight: 400, marginLeft: 4 }}>
                      ({sessionData.guestId})
                    </span>
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right: Session Timer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={<AccessTimeIcon />}
              label={`${timeRemaining} min remaining`}
              size="small"
              color={showWarning ? 'warning' : 'default'}
              sx={{
                fontWeight: 600,
                ...(showWarning && {
                  bgcolor: '#ff9800',
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' }
                })
              }}
            />
          </Box>
        </Paper>

        {/* Session Warning */}
        {showWarning && (
          <Alert
            severity="warning"
            sx={{ borderRadius: 0, justifyContent: 'center' }}
          >
            Your session will expire in {timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}.
            You will be automatically logged out.
          </Alert>
        )}

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ExternalDoctorDesk;
